import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Archive, Loader2, Search, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME, PROJECT_TYPES, ACTIVITY_TYPES } from "../../lib/labels";
import { EmptyState } from "../../components/dash/ui";
import { PhotoHistory, PHASES, MONTHS } from "../../components/coffee/PhotoHistory";
import { YearCompare } from "../../components/coffee/YearCompare";

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors ${active ? "bg-emerald-800 border-emerald-800 text-white" : "bg-white text-emerald-900 hover:bg-emerald-50"}`}>
    {children}
  </button>
);

const YearPicker = ({ years, year, setYear }) => (
  <div className="flex flex-wrap gap-2" data-testid="archive-year-picker">
    <Chip active={!year} onClick={() => setYear("")} testid="archive-year-all">Semua Tahun</Chip>
    {years.map((y) => <Chip key={y.year} active={year === y.year} onClick={() => setYear(y.year)} testid={`archive-year-${y.year}`}>{y.year} ({y.activities})</Chip>)}
  </div>
);

const sel = "h-11 w-full rounded-lg border bg-white px-3 text-sm";
const SearchBar = ({ month, type, set }) => (
  <div className="bg-white border rounded-xl p-3 sm:p-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid="archive-search">
    <label className="block"><span className="text-xs font-semibold text-slate-600 inline-flex items-center gap-1"><Search className="h-3 w-3" />Bulan</span>
      <select value={month} onChange={(e) => set("bulan", e.target.value)} className={sel} data-testid="archive-month-select">
        <option value="">Semua Bulan</option>
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </label>
    <label className="block"><span className="text-xs font-semibold text-slate-600">Jenis Kegiatan</span>
      <select value={type} onChange={(e) => set("jenis", e.target.value)} className={sel} data-testid="archive-type-select">
        <option value="">Semua Kegiatan</option>
        {PHASES.map(([k, l, types]) => <optgroup key={k} label={l}>{types.filter((t) => ACTIVITY_TYPES[t]).map((t) => <option key={t} value={t}>{ACTIVITY_TYPES[t]}</option>)}</optgroup>)}
      </select>
    </label>
    {(month || type) && <button type="button" onClick={() => set("bulan", "", { jenis: "" })} className="h-11 px-4 rounded-full border text-sm font-semibold text-slate-700 inline-flex items-center gap-1" data-testid="archive-search-reset"><X className="h-4 w-4" />Hapus filter</button>}
  </div>
);

const ProjectArchive = ({ projectId, title, year, month, type, set, base }) => {
  const [years, setYears] = useState(null);
  useEffect(() => { setYears(null); api.get(`/projects/${projectId}/photo-years`).then((r) => setYears(r.data)).catch(() => setYears([])); }, [projectId]);
  if (!years) return <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />;
  return <>
    {years.length >= 2 && <YearCompare key={projectId} projectId={projectId} title={title} years={years.map((y) => y.year)} />}
    <YearPicker years={years} year={year} setYear={(v) => set("tahun", v)} />
    <SearchBar month={month} type={type} set={set} />
    <PhotoHistory key={`${projectId}-${year}-${month}-${type}`} projectId={projectId} base={base} year={year} month={month} type={type} />
  </>;
};

export default function PhotoArchive() {
  const { user } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [projects, setProjects] = useState(null);
  const projectId = search.get("proyek") || "";
  const set = (k, v, extra = {}) => setSearch((prev) => {
    const n = new URLSearchParams(prev);
    Object.entries({ [k]: v, ...extra }).forEach(([key, val]) => (val ? n.set(key, val) : n.delete(key)));
    if (k === "proyek") ["tahun", "bulan", "jenis"].forEach((x) => n.delete(x));
    return n;
  }, { replace: true });
  useEffect(() => {
    api.get(user.role === "SUPER_ADMIN" ? "/admin/projects" : "/me/projects").then((r) => {
      setProjects(r.data);
      if (r.data[0] && !r.data.some((p) => p.id === projectId)) set("proyek", r.data[0].id);
    }).catch(() => setProjects([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!projects) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  const current = projects.find((p) => p.id === projectId);
  return (
    <div className="space-y-5" data-testid="photo-archive-page">
      <header>
        <p className="text-xs font-bold uppercase tracking-wide text-amber-700 inline-flex items-center gap-1.5"><Archive className="h-4 w-4" />Arsip Foto Kebun</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Riwayat foto dari tahun ke tahun</h1>
        <p className="text-sm text-slate-600 mt-1">Semua foto dan video bukti tersimpan permanen. Pilih kebun dan tahun, lalu cari per bulan atau jenis kegiatan.</p>
      </header>
      {!projects.length ? <EmptyState text="Belum ada kebun kopi yang dapat Anda lihat." testid="archive-empty" /> : <>
        <label className="block max-w-xl">
          <span className="text-xs font-semibold text-slate-600">Kebun</span>
          <select value={projectId} onChange={(e) => set("proyek", e.target.value)} className={`mt-1 ${sel}`} data-testid="archive-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.projectCode} · {p.name}</option>)}
          </select>
        </label>
        {current && <p className="text-xs text-slate-500" data-testid="archive-project-type">{PROJECT_TYPES[current.projectType] || PROJECT_TYPES.INVESTMENT_COFFEE_PROJECT}</p>}
        {current && <ProjectArchive projectId={projectId} title={`${current.projectCode} · ${current.name}`} year={search.get("tahun") || ""} month={search.get("bulan") || ""} type={search.get("jenis") || ""} set={set} base={ROLE_HOME[user.role]} />}
      </>}
    </div>
  );
}
